import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useMediaQuery,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import theme from "./Theme";
import OrangeHighlightButton from "./OrangeHighlightButton";

interface ChristmasPromoCardProps {
  onClose?: () => void;
}

const ChristmasPromoCard: React.FC<ChristmasPromoCardProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleEnrollClick = () => {
    // Navigate to enroll page with language prefix
    const languagePrefix = i18n.language === "en" ? "" : `/${i18n.language}`;
    history.push(`${languagePrefix}/enroll`);
    if (onClose) onClose();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: "#FFFAF4",
        position: "relative",
        overflow: "hidden",
        minWidth: isMobile ? "90vw" : "600px",
        maxWidth: isMobile ? "95vw" : "700px",
        p: isMobile ? 3 : 4,
        borderRadius: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Decorative bird */}
      {!isMobile && (
        <Box
          component="img"
          src="/bird.svg"
          alt="Bird"
          sx={{
            position: "absolute",
            bottom: 40,
            right: -20,
            width: "180px",
            height: "auto",
            opacity: 0.3,
            zIndex: 0,
            rotate: "-20deg",
          }}
        />
      )}

      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          zIndex: 1,
          width: "100%",
        }}
      >
        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#E45A2A",
            mb: 3,
            fontSize: isMobile ? "1.5rem" : "2rem",
            lineHeight: 1.3,
          }}
        >
          {t("ChristmasPromo.title")}
        </Typography>

        {/* Discount badge */}
        <Box
          sx={{
            backgroundColor: "#E45A2A",
            color: "white",
            borderRadius: "16px",
            px: 4,
            py: 2,
            mb: 3,
            boxShadow: "0 4px 12px rgba(228, 90, 42, 0.3)",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: isMobile ? "2.5rem" : "3.5rem",
              lineHeight: 1,
            }}
          >
            {t("ChristmasPromo.discount")}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            fontSize: isMobile ? "1rem" : "1.125rem",
            color: "#333",
            mb: 2,
            lineHeight: 1.6,
            maxWidth: "500px",
          }}
        >
          {t("ChristmasPromo.description")}
        </Typography>

        {/* Benefits */}
        <Stack spacing={1.5} sx={{ mb: 3, width: "100%", maxWidth: "500px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                color: "#E45A2A",
                fontWeight: 700,
                mr: 1,
                fontSize: "1.25rem",
              }}
            >
              ✔
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: isMobile ? "0.9rem" : "1rem", color: "#555" }}
            >
              {t("ChristmasPromo.benefit1")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                color: "#E45A2A",
                fontWeight: 700,
                mr: 1,
                fontSize: "1.25rem",
              }}
            >
              ✔
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: isMobile ? "0.9rem" : "1rem", color: "#555" }}
            >
              {t("ChristmasPromo.benefit2")}
            </Typography>
          </Box>
        </Stack>

        {/* Call to action */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: "#E45A2A",
            mb: 3,
            fontSize: isMobile ? "1rem" : "1.125rem",
          }}
        >
          {t("ChristmasPromo.cta")}
        </Typography>

        {/* Enroll button */}
        <OrangeHighlightButton onClick={handleEnrollClick}>
          <Typography variant="h5">{t("ChristmasPromo.buttonText")}</Typography>
        </OrangeHighlightButton>
      </Box>
    </Paper>
  );
};

export default ChristmasPromoCard;
